/**
 * The inventory as the engine prepares it (Foundry: actor/inventory/index.ts, bulk.ts, and the
 * character's hands, investiture, shield and apex handling): every physical Entry the Character
 * owns as an EnginePhysical (weapons as EngineWeapon), containers linked, bulk totalled against
 * the Strength thresholds, hands counted, investiture counted, wealth summed. The engine applies
 * the items' Rule Elements only while they are equipped (and invested) as Foundry requires.
 */
import type { Entry } from "../../shared/snapshot-format";
import type { Inventory, InventoryItem } from "../character/model";
import { Bulk, type Size } from "./bulk";
import type { Catalogue } from "./catalogue";
import { Coins, type RawCoins } from "./coins";
import { prepareConditionSiblings, setEffectRollOptions } from "./conditions";
import type { EquippedInput } from "./equipment";
import { EngineItem } from "./items";
import { localize, tableLabel } from "./labels";
import { EnginePhysical, type CarryType, type EnginePhysicalInput, type ItemRunes, type UsageDetails } from "./physical";
import { ARMOR_PROPERTY_RUNES, RUNE_DATA, WEAPON_PROPERTY_RUNES, propertyRuneSlots } from "./runes-data";
import { sluggify } from "./slug";
import type { EngineState } from "./state";
import { isAttribute } from "./values";
import { EngineWeapon, weaponEntry } from "./weapons";

export interface InventoryBulk {
  total: Bulk;
  encumberedAfter: number;
  max: number;
  encumbered: boolean;
  overMax: boolean;
}

/** The inventory in engine state. */
export interface InventoryState {
  items: EnginePhysical[];
  byId: Map<string, EnginePhysical>;
  /** Inventory items whose Entry was not supplied. */
  missing: { id: string; uuid: string }[];
  coins: Coins;
  wornArmor: EnginePhysical | null;
  heldShield: EnginePhysical | null;
  bulk: InventoryBulk;
  hands: { max: number; free: number; reallyFree: number };
  investiture: { value: number; max: number };
  /** Set once the encumbered Condition was imposed (or wanted and not loadable). */
  encumberedImposed: boolean;
}

export function emptyInventoryState(): InventoryState {
  return { items: [], byId: new Map(), missing: [], coins: new Coins(), wornArmor: null, heldShield: null, bulk: { total: new Bulk(), encumberedAfter: 5, max: 10, encumbered: false, overMax: false }, hands: { max: 2, free: 2, reallyFree: 2 }, investiture: { value: 0, max: 10 }, encumberedImposed: false };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** The engine input for one inventory item. */
function inputFor(item: InventoryItem, entry: Entry): EnginePhysicalInput & { traitToggles?: InventoryItem["traitToggles"] } {
  return {
    entry,
    inventoryId: item.id,
    quantity: item.quantity,
    carryType: item.carryType,
    handsHeld: item.handsHeld,
    inSlot: item.inSlot,
    invested: item.invested,
    containerId: item.container ?? null,
    size: item.size,
    hp: item.hp,
    runes: item.runes,
    material: item.material ? { type: item.material.type, grade: item.material.grade } : undefined,
    ammo: item.ammo ?? null,
    traitToggles: item.traitToggles,
  };
}

function construct(input: EnginePhysicalInput & { traitToggles?: InventoryItem["traitToggles"] }): EnginePhysical {
  return input.entry.type === "weapon" ? new EngineWeapon(input) : new EnginePhysical(input);
}

/** The Level physical items are prepared at under Automatic Bonus Progression (#12), else null. */
export function abpLevelOf(state: EngineState): number | null {
  return state.variantRules.automaticBonusProgression ? state.level : null;
}

/**
 * Build the inventory's items from the Character's inventory and any direct `equipped` input
 * (tests), link containers, and add every item to the engine's item list so their Rule Elements
 * are parsed with the rest. Physical Entries need no Choice: they are not Selections.
 */
export function buildInventory(state: EngineState, inventory: Inventory | undefined, equipped: EquippedInput | undefined): InventoryState {
  const abpLevel = abpLevelOf(state);
  const out = emptyInventoryState();
  out.coins = new Coins(inventory?.coins ?? {});
  for (const item of inventory?.items ?? []) {
    const entry = state.entries.get(item.uuid);
    if (!entry) {
      out.missing.push({ id: item.id, uuid: item.uuid });
      continue;
    }
    if (!(["ammo", "armor", "backpack", "book", "consumable", "equipment", "shield", "treasure", "weapon"] as readonly string[]).includes(entry.type)) {
      out.missing.push({ id: item.id, uuid: item.uuid });
      continue;
    }
    out.items.push(construct({ ...inputFor(item, entry), abpLevel }));
  }
  // Direct inputs: worn armour in its slot, a held shield, weapons as described.
  if (equipped?.armor) out.items.push(new EnginePhysical({ entry: equipped.armor, carryType: "worn", inSlot: true, invested: true, abpLevel }));
  if (equipped?.shield) out.items.push(new EnginePhysical({ entry: equipped.shield, carryType: "held", handsHeld: 1, abpLevel }));
  for (const w of equipped?.weapons ?? []) {
    const entry = w.id ? { ...w.entry, id: w.id } : w.entry;
    out.items.push(new EngineWeapon({ entry, inventoryId: w.id, carryType: w.carryType, handsHeld: w.handsHeld, invested: w.invested, quantity: w.quantity, traitToggles: w.traitToggles, runes: w.runes, material: w.material, abpLevel }));
  }
  for (const item of out.items) out.byId.set(item.inventoryId, item);
  // Containers: link contents, refusing cycles.
  for (const item of out.items) {
    const container = item.containerId ? out.byId.get(item.containerId) : undefined;
    if (!container || container === item || container.type !== "backpack") {
      if (container === item) item.itemProblems.push("a container cannot hold itself");
      item.containerId = null;
      continue;
    }
    let ancestor: EnginePhysical | null = container;
    let cycle = false;
    for (let depth = 0; ancestor && depth < 64; depth++) {
      if (ancestor === item) { cycle = true; break; }
      ancestor = ancestor.containerId ? (out.byId.get(ancestor.containerId) ?? null) : null;
    }
    if (cycle) {
      item.containerId = null;
      item.itemProblems.push("a container cannot hold itself");
      continue;
    }
    item.container = container;
    container.contents.push(item);
  }
  // Foundry's prepareSiblingData: an item inside a stowing container is stowed.
  for (const item of out.items) {
    if (item.container && item.isStowed && item.carryType !== "stowed") {
      item.carryType = "stowed";
      item.handsHeld = 0;
      if (item.inSlot !== null) item.inSlot = false;
      (item.system.equipped as Record<string, unknown>).carryType = "stowed";
      (item.system.equipped as Record<string, unknown>).handsHeld = 0;
    }
  }
  // Ammunition: a weapon's selected stack lends its Rule Elements and roll options (Foundry: WeaponPF2e#prepareSiblingData).
  for (const item of out.items) {
    if (!(item instanceof EngineWeapon) || !item.ammo) continue;
    const ammo = out.byId.get(item.ammo);
    if (!ammo || !ammoMatches(ammo, item)) continue;
    item.ammoItem = ammo;
    const rules = Array.isArray(item.system.rules) ? item.system.rules : [];
    item.system.rules = [...rules, ...ammo.rules.map((r) => ({ label: ammo.name, ...structuredClone(r) }))];
  }
  for (const item of out.items) item.generatedName = item.generateName(state.catalogue);
  out.wornArmor = out.items.find((i) => i.type === "armor" && i.isEquipped) ?? null;
  out.heldShield = bestShield(out.items.filter((i) => i.type === "shield" && i.isEquipped));
  state.items.push(...out.items);
  return out;
}

/** Foundry's `heldShield`: with several, the best AC bonus, then the most HP, then the most hardness. */
function bestShield(shields: EnginePhysical[]): EnginePhysical | null {
  if (shields.length === 0) return null;
  return shields.reduce((best, shield) => {
    const ac = (s: EnginePhysical): number => Number(s.system.acBonus) || 0;
    if (ac(shield) !== ac(best)) return ac(shield) > ac(best) ? shield : best;
    if (shield.hitPoints.value !== best.hitPoints.value) return shield.hitPoints.value > best.hitPoints.value ? shield : best;
    return shield.hardness > best.hardness ? shield : best;
  });
}

/** Whether an ammunition stack fits a weapon (Foundry: AmmoPF2e#isAmmoFor). */
export function ammoMatches(ammo: EnginePhysical, weapon: EngineWeapon): boolean {
  if (ammo.type !== "ammo") return false;
  const weaponAmmo = isRecord(weapon.system.ammo) ? weapon.system.ammo : null;
  if (!weaponAmmo || weaponAmmo.builtIn === true) return false;
  const weaponBase = typeof weaponAmmo.baseType === "string" ? weaponAmmo.baseType : null;
  const ammoBase = ammo.baseType;
  const parent = (base: string | null): string | null => (!base ? null : base.startsWith("rounds-") ? "rounds" : /-magazine$|^magazine-/.test(base) ? "magazine" : null);
  const uses = isRecord(ammo.system.uses) ? ammo.system.uses : {};
  const isMagazine = (typeof uses.max === "number" && uses.max > 1) || ["magazine", "battery", "chem-tank"].includes(ammoBase ?? "") || parent(ammoBase) === "magazine";
  const craftableAs = Array.isArray(ammo.system.craftableAs) ? ammo.system.craftableAs.filter((c): c is string => typeof c === "string") : null;
  const basicAmmoType = parent(ammoBase) ?? ammoBase;
  const basicWeaponType = parent(weaponBase) ?? weaponBase;
  if (!ammoBase && craftableAs && !isMagazine && weaponBase) return craftableAs.length === 0 || !basicWeaponType || craftableAs.includes(basicWeaponType);
  if (ammoBase === weaponBase) return true;
  if (!isMagazine && weaponBase === null) return true;
  return basicAmmoType === basicWeaponType && (parent(ammoBase) === null || parent(weaponBase) === null);
}

/**
 * Phase 1 (Foundry: prepareActorData of shields and apex items): the held shield's data on the
 * actor, an invested apex item's attribute, and the worn armour's `armor:*` roll options.
 */
export function prepareInventoryActorData(state: EngineState): void {
  const inventory = state.inventory;
  const { actor } = state;
  const shield = inventory.heldShield;
  if (shield) {
    const hp = shield.hitPoints;
    actor.system.attributes.shield = { itemId: shield.inventoryId, name: shield.displayName, ac: Number(shield.system.acBonus) || 0, hp: { value: hp.value, max: hp.max }, hardness: shield.hardness, brokenThreshold: hp.brokenThreshold, raised: false, broken: shield.isBroken, destroyed: shield.isDestroyed };
    state.rollOptions.set("all", "self:shield:equipped");
    if (shield.isDestroyed) state.rollOptions.set("all", "self:shield:destroyed");
    else if (shield.isBroken) state.rollOptions.set("all", "self:shield:broken");
  }
  // One apex item applies: the first invested one (or equipped, for one that needs no investing). Under ABP the apex is the Level 17 Choice's (#12).
  const apex = state.variantRules.automaticBonusProgression ? undefined : inventory.items.find((i) => i.apexAttribute && (i.isInvested === true || (i.isInvested === null && i.isEquipped)));
  const attribute = apex?.apexAttribute;
  if (apex && isAttribute(attribute) && !actor.system.build.attributes.apex) actor.system.build.attributes.apex = attribute;
}

/** After the applyAEs phase: hands (Foundry: prepareHandsData) and the raised-shield option. */
export function prepareHands(state: EngineState): void {
  const { actor, inventory } = state;
  const maxHands = actor.system.hands.max.value;
  let held = 0;
  let reallyHeld = 0;
  for (const item of inventory.items) {
    const hands = item.isHeld ? item.handsHeld : 0;
    if (!hands) continue;
    reallyHeld += hands;
    if (item.type !== "shield" && !item.traits.includes("free-hand")) held += hands;
  }
  const clamp = (n: number): number => Math.max(0, Math.min(maxHands, n));
  inventory.hands = { max: maxHands, free: clamp(maxHands - held), reallyFree: clamp(maxHands - reallyHeld) };
  actor.system.hands.free = { value: inventory.hands.free, really: inventory.hands.reallyFree };
  state.handsReallyFree = inventory.hands.reallyFree;
  const shield = actor.system.attributes.shield;
  if (shield.raised && !shield.broken && !shield.destroyed) state.rollOptions.set("all", "self:shield:raised");
}

/** Foundry's `InventoryBulk.computeTotalBulk` over the items not in a container. */
export function computeTotalBulk(items: EnginePhysical[], actorSize: Size): Bulk {
  const flattened = flattenNonStowing(items);
  const nonStacking = flattened.filter((i) => i.type === "backpack" || (i.bulkData.per === 1 && !!i.baseType));
  const nonStackingIds = new Set(nonStacking);
  const stacking = flattened.filter((i) => !nonStackingIds.has(i));
  let total = nonStacking.reduce((sum, item) => sum.plus(itemBulk(item, actorSize)), new Bulk());
  const groups = new Map<string, { bulk: Bulk; per: number; quantity: number }>();
  for (const item of stacking) {
    const bulk = new Bulk(item.bulkData.value).convertToSize(item.size, actorSize);
    const key = `${item.baseType}-${item.bulkData.per}-${bulk.toLightUnits()}`;
    const group = groups.get(key) ?? { bulk, per: item.bulkData.per, quantity: 0 };
    group.quantity += item.quantity;
    groups.set(key, group);
  }
  for (const group of groups.values()) total = total.plus(group.bulk.times(Math.floor(group.quantity / group.per)));
  return total;
}

function flattenNonStowing(items: EnginePhysical[]): EnginePhysical[] {
  return items.flatMap((item) => (item.type === "backpack" && !item.stowsItems ? flattenNonStowing(item.contents) : [item]));
}

/** An item's bulk with its contents (Foundry: ContainerPF2e#bulk): own bulk plus what it holds, less what it ignores. */
export function itemBulk(item: EnginePhysical, actorSize: Size): Bulk {
  const own = item.ownBulk(actorSize);
  if (item.type !== "backpack") return own;
  const contents = computeTotalBulk(item.contents, actorSize);
  return own.plus(contents.minus(ignoredBulk(item, contents)));
}

/** How much of its contents a container takes off the bearer: nothing when overfilled or nested in another extradimensional space. */
export function ignoredBulk(container: EnginePhysical, contents: Bulk): Bulk {
  const max = new Bulk(container.bulkData.capacity);
  const realPercent = Math.floor((contents.toLightUnits() / max.toLightUnits()) * 100);
  const percentFull = realPercent > 100 ? Math.floor((contents.normal / max.normal) * 100) : realPercent;
  const overfilled = percentFull > 100;
  const paradox = container.traits.includes("extradimensional") && hasExtradimensionalParent(container);
  return overfilled || paradox ? new Bulk() : new Bulk(container.bulkData.ignored);
}

function hasExtradimensionalParent(item: EnginePhysical, seen = new Set<EnginePhysical>()): boolean {
  if (seen.has(item)) return false;
  seen.add(item);
  const parent = item.container;
  if (!parent) return false;
  if (parent.traits.includes("extradimensional")) return true;
  return hasExtradimensionalParent(parent, seen);
}

/**
 * After abilities are final: total bulk against the Strength thresholds, the investiture count,
 * and the encumbered Condition when the total passes the threshold (Foundry: imposeEncumberedCondition,
 * an in-memory Condition from the conditions Pack). The Condition's own Rule Elements bring the
 * speed penalty and clumsy 1.
 */
export function prepareBulk(state: EngineState): void {
  const { actor, inventory } = state;
  const strength = actor.system.abilities.str.mod;
  const size = actor.system.traits.size.value as Size;
  const total = computeTotalBulk(inventory.items.filter((i) => !i.isInContainer), size);
  const encumberedAfter = Math.floor(strength + 5 + actor.inventory.bulk.encumberedAfterAddend);
  const max = Math.floor(strength + 10 + actor.inventory.bulk.maxAddend);
  inventory.bulk = { total, encumberedAfter, max, encumbered: total.normal > encumberedAfter, overMax: total.normal > max };
  inventory.investiture = { value: inventory.items.filter((i) => i.isInvested === true).length, max: actor.system.resources.investiture.max };
  actor.system.resources.investiture.value = inventory.investiture.value;
  if (inventory.bulk.encumbered) imposeEncumbered(state);
}

function imposeEncumbered(state: EngineState): void {
  const { inventory } = state;
  if (inventory.encumberedImposed) return;
  inventory.encumberedImposed = true;
  if (state.items.some((i) => i.type === "condition" && i.slug === "encumbered" && i.active)) return;
  const record = state.catalogue?.records.find((r) => r.t === "condition" && r.p === "conditionitems" && r.s === "encumbered");
  const uuid = record?.u ?? [...state.entries.values()].find((e) => e.type === "condition" && e.slug === "encumbered")?.uuid;
  if (!uuid) return;
  const entry = state.entries.get(uuid);
  if (!entry) {
    state.needed.add(uuid);
    return;
  }
  const item = new EngineItem({ entry });
  item.inMemory = true;
  state.items.push(item);
  prepareConditionSiblings(state);
  if (!item.active) return;
  setEffectRollOptions(state, item);
  // Its Rule Elements missed the applyAEs phase: run it for them now (Foundry does the same).
  for (const rule of state.addRules(item)) {
    if (rule.ignored) continue;
    try {
      rule.handler?.onApplyActiveEffects?.(rule, state);
    } catch (error) {
      rule.fail(`onApplyActiveEffects failed: ${error instanceof Error ? error.message : String(error)}`);
    }
  }
}

/** Foundry's `ShieldPF2e#generateWeapon`: the shield bash (or an integrated weapon) of a held shield. */
export function shieldWeapon(state: EngineState, shield: EnginePhysical): EngineWeapon | null {
  if (shield.isStowed || shield.isBroken || shield.isDestroyed) return null;
  const weaponTraitTable = state.catalogue?.table("weaponTraits");
  const shieldTraits = shield.traits;
  const weaponTraits = shieldTraits.filter((t) => (weaponTraitTable ? t in weaponTraitTable : ["magical", "arcane", "divine", "occult", "primal"].includes(t)));
  const throwTrait = shieldTraits.find((t) => t.startsWith("shield-throw-"));
  if (throwTrait) weaponTraits.push(`thrown-${throwTrait.slice("shield-throw-".length)}`);
  const integrated = isRecord(shield.system.traits) && isRecord((shield.system.traits as Record<string, unknown>).integrated) ? ((shield.system.traits as Record<string, unknown>).integrated as Record<string, unknown>) : null;
  const integratedTrait = shieldTraits.find((t) => t.startsWith("integrated"));
  const hp = shield.hitPoints;
  const system: Record<string, unknown> = {
    category: "martial",
    group: "shield",
    baseItem: shield.baseType,
    equipped: { carryType: shield.carryType, handsHeld: shield.handsHeld, invested: null },
    hp: { value: hp.value, max: hp.max },
    material: { type: shield.material.type, grade: shield.material.grade },
    traits: { rarity: shield.itemRarity, value: [...new Set(weaponTraits)].sort(), otherTags: [] },
    damage: { dice: 1, die: "d4", damageType: "bludgeoning", modifier: 0, persistent: null },
    bulk: { value: shield.bulkData.value },
    level: { value: shield.itemLevel },
    usage: { value: "held-in-one-hand" },
    runes: { potency: 0, striking: 0, property: [] },
  };
  if (integratedTrait) {
    const parts = integratedTrait.split("-");
    const map: Record<string, string> = { b: "bludgeoning", p: "piercing", s: "slashing" };
    const damageType = map[parts[2] ?? ""] ?? "slashing";
    system.damage = { dice: 1, die: "d6", damageType, modifier: 0, persistent: null };
    if (integrated && isRecord(integrated.runes)) system.runes = { potency: 0, striking: 0, property: [], ...integrated.runes };
    const versatile = shieldTraits.find((t) => t.includes("versatile"));
    if (versatile) (system.traits as { value: string[] }).value.push(versatile.slice(versatile.indexOf("versatile")));
  }
  const entry = weaponEntry(shield.id, shield.displayName, shield.slug, system, shield.uuid);
  const weapon = new EngineWeapon({ entry, inventoryId: shield.inventoryId, carryType: shield.carryType, handsHeld: shield.handsHeld, quantity: 1, size: shield.size });
  weapon.name = shield.displayName;
  return weapon;
}

/* ----------------------------------- Views ----------------------------------- */

export interface InventoryItemView {
  id: string;
  uuid: string;
  /** The Entry's name, or the name generated from base type, runes and material. */
  name: string;
  entryName: string;
  type: string;
  level: number;
  rarity: string;
  traits: string[];
  quantity: number;
  carryType: CarryType;
  handsHeld: number;
  inSlot: boolean | null;
  invested: boolean | null;
  /** Foundry's isEquipped: worn in its slot, held in enough hands, or merely carried. */
  equipped: boolean;
  stowed: boolean;
  /** Whether the item's Rule Elements apply (equipped and, if it needs it, invested). */
  active: boolean;
  rules: number;
  container: string | null;
  contents: string[];
  usage: UsageDetails;
  usageLabel: string;
  size: string;
  bulk: { unit: number; unitLabel: string; total: number; totalLabel: string; per: number; heldOrStowed: number };
  price: { unit: RawCoins; unitLabel: string; stack: RawCoins; stackLabel: string; per: number };
  runes: ItemRunes;
  runeSlots: number;
  runeLabels: string[];
  material: { type: string | null; grade: string | null; label: string | null };
  specific: boolean;
  hp: { value: number; max: number; brokenThreshold: number } | null;
  hardness: number;
  broken: boolean;
  destroyed: boolean;
  apex: string | null;
  stowsItems: boolean;
  capacity: { value: number; max: number; ignored: number } | null;
  armor: { category: string; acBonus: number; dexCap: number; checkPenalty: number; speedPenalty: number; strength: number | null; group: string | null } | null;
  shield: { acBonus: number; speedPenalty: number; raised: boolean } | null;
  /** Weapons: the ammunition it takes, if any, and the compatible stacks held. */
  ammunition: { baseType: string | null; selected: string | null; compatible: { id: string; name: string; quantity: number }[] } | null;
  problems: string[];
}

export interface InventoryView {
  items: InventoryItemView[];
  coins: RawCoins;
  coinsLabel: string;
  wealth: { coins: number; items: number; total: number; label: string };
  bulk: { total: number; label: string; encumberedAfter: number; max: number; encumbered: boolean; overMax: boolean; breakdown: string };
  hands: { max: number; free: number; reallyFree: number };
  investiture: { value: number; max: number };
  wornArmor: string | null;
  heldShield: string | null;
  missing: { id: string; uuid: string }[];
}

export function inventoryView(state: EngineState): InventoryView {
  const { inventory, actor, catalogue } = state;
  const size = actor.system.traits.size.value as Size;
  const strength = actor.system.abilities.str.mod;
  const items = inventory.items.map((item) => itemView(state, item, size, catalogue));
  const itemsValue = inventory.items.reduce((sum, i) => sum.plus(i.assetValue), new Coins());
  const total = inventory.coins.plus(itemsValue);
  const encumbrance = inventory.bulk;
  return {
    items,
    coins: inventory.coins.toObject(),
    coinsLabel: inventory.coins.toString({ normalize: false }),
    wealth: { coins: inventory.coins.copperValue, items: itemsValue.copperValue, total: total.copperValue, label: total.toGoldString() },
    bulk: { total: encumbrance.total.value, label: encumbrance.total.toString(), encumberedAfter: encumbrance.encumberedAfter, max: encumbrance.max, encumbered: encumbrance.encumbered, overMax: encumbrance.overMax, breakdown: `${localize(state, "PF2E.BulkMaxLabel", "Max Bulk")}: 10 + ${strength} (Strength)${actor.inventory.bulk.maxAddend ? ` + ${actor.inventory.bulk.maxAddend}` : ""}; encumbered above 5 + ${strength}${actor.inventory.bulk.encumberedAfterAddend ? ` + ${actor.inventory.bulk.encumberedAfterAddend}` : ""}` },
    hands: { ...inventory.hands },
    investiture: { ...inventory.investiture },
    wornArmor: inventory.wornArmor?.inventoryId ?? null,
    heldShield: inventory.heldShield?.inventoryId ?? null,
    missing: inventory.missing.map((m) => ({ ...m })),
  };
}

function usageLabel(state: EngineState, usage: UsageDetails): string {
  switch (usage.type) {
    case "held": return usage.hands === 2 ? "Held in two hands" : usage.value === "held-in-one-plus-hands" ? "Held in one or two hands" : "Held in one hand";
    case "worn": return usage.where ? `Worn (${tableLabel(state, "wornSlots", usage.where)})` : "Worn";
    case "carried": return "Carried";
    case "implanted": return "Implanted";
    case "attached": return `Attached to ${usage.where?.replace(/-/g, " ") ?? "an item"}`;
    case "installed": return `Installed in ${usage.where?.replace(/-/g, " ") ?? "an item"}`;
  }
}

function itemView(state: EngineState, item: EnginePhysical, size: Size, catalogue: Catalogue | null): InventoryItemView {
  const unit = new Bulk(item.bulkData.value).convertToSize(item.size, size);
  const total = itemBulk(item, size);
  const capacity = item.type === "backpack" ? computeTotalBulk(item.contents, size) : null;
  const runes = item.runes;
  const runeTable = item.type === "armor" ? ARMOR_PROPERTY_RUNES : WEAPON_PROPERTY_RUNES;
  const loc = (key: string | undefined | null): string | null => (key ? (catalogue?.localize(key) ?? key) : null);
  const runeLabels: string[] = [];
  if (item.type === "weapon" || item.type === "armor") {
    if (runes.potency) runeLabels.push(`+${runes.potency} ${item.type === "weapon" ? "weapon" : "armor"} potency`);
    if (item.type === "weapon" && runes.striking) runeLabels.push(loc(RUNE_DATA.weapon.striking[runes.striking as 1 | 2 | 3 | 4]?.name) ?? "striking");
    if (item.type === "armor" && runes.resilient) runeLabels.push(loc(RUNE_DATA.armor.resilient[runes.resilient as 1 | 2 | 3 | 4]?.name) ?? "resilient");
    for (const p of runes.property) runeLabels.push(loc(runeTable[p]?.name) ?? p);
  } else if (item.type === "shield" && runes.reinforcing) {
    runeLabels.push(loc(RUNE_DATA.shield.reinforcing[runes.reinforcing as 1 | 2 | 3 | 4 | 5 | 6]?.name) ?? "reinforcing");
  }
  const material = item.material;
  const materialLabel = material.type ? `${material.grade ? `${tableLabel(state, "preciousMaterialGrades", material.grade)} ` : ""}${tableLabel(state, "preciousMaterials", material.type)}` : null;
  const rules = state.rules.filter((r) => r.item === item);
  const active = item.isEquipped && item.carryType !== "dropped" && item.isInvested !== false;
  const weapon = item instanceof EngineWeapon ? item : null;
  const weaponAmmo = weapon && isRecord(weapon.system.ammo) ? weapon.system.ammo : null;
  const hp = item.hitPoints;
  return {
    id: item.inventoryId,
    uuid: item.uuid,
    name: item.displayName,
    entryName: item.entry.name,
    type: item.type,
    level: item.itemLevel,
    rarity: item.itemRarity,
    traits: [...item.traits],
    quantity: item.quantity,
    carryType: item.carryType,
    handsHeld: item.handsHeld,
    inSlot: item.inSlot,
    invested: item.isInvested,
    equipped: item.isEquipped,
    stowed: item.isStowed,
    active,
    rules: rules.length,
    container: item.container?.inventoryId ?? null,
    contents: item.contents.map((c) => c.inventoryId),
    usage: { ...item.usage },
    usageLabel: usageLabel(state, item.usage),
    size: item.size,
    bulk: { unit: unit.value, unitLabel: unit.toString(), total: total.value, totalLabel: total.toString(), per: item.bulkData.per, heldOrStowed: item.bulkData.heldOrStowed },
    price: { unit: item.price.toObject(), unitLabel: item.price.toString(), stack: item.assetValue.toObject(), stackLabel: item.assetValue.toString(), per: item.pricePer },
    runes: { ...runes, property: [...runes.property] },
    runeSlots: item.type === "weapon" || item.type === "armor" ? propertyRuneSlots({ material, runes }) : 0,
    runeLabels,
    material: { type: material.type, grade: material.grade, label: materialLabel },
    specific: item.isSpecific,
    hp: hp.max > 0 ? { ...hp } : null,
    hardness: item.hardness,
    broken: item.isBroken,
    destroyed: item.isDestroyed,
    apex: item.apexAttribute,
    stowsItems: item.stowsItems,
    capacity: capacity ? { value: capacity.value, max: item.bulkData.capacity, ignored: ignoredBulk(item, capacity).value } : null,
    armor: item.type === "armor" ? { category: item.category ?? "unarmored", acBonus: Number(item.system.acBonus) || 0, dexCap: Number(item.system.dexCap) || 0, checkPenalty: Number(item.system.checkPenalty) || 0, speedPenalty: Number(item.system.speedPenalty) || 0, strength: typeof item.system.strength === "number" ? item.system.strength : null, group: item.group } : null,
    shield: item.type === "shield" ? { acBonus: Number(item.system.acBonus) || 0, speedPenalty: Number(item.system.speedPenalty) || 0, raised: state.actor.system.attributes.shield.itemId === item.inventoryId && state.actor.system.attributes.shield.raised } : null,
    ammunition: weapon && weaponAmmo && weaponAmmo.builtIn !== true ? { baseType: typeof weaponAmmo.baseType === "string" ? weaponAmmo.baseType : null, selected: weapon.ammoItem?.inventoryId ?? null, compatible: state.inventory.items.filter((a) => ammoMatches(a, weapon)).map((a) => ({ id: a.inventoryId, name: a.displayName, quantity: a.quantity })) } : null,
    problems: [...item.itemProblems],
  };
}

/** A rune's display label, for the sheet and the Runes UI. */
export function runeLabel(catalogue: Catalogue | null, type: "weapon" | "armor", slug: string): string {
  const table = type === "weapon" ? WEAPON_PROPERTY_RUNES : ARMOR_PROPERTY_RUNES;
  const key = table[slug]?.name;
  const text = key ? (catalogue?.localize(key) ?? key) : slug;
  return text.startsWith("PF2E.") ? sluggify(slug).replace(/-/g, " ") : text;
}
